import { axiosInstance } from './axiosInstance';
import type { Event } from '../types/api';
import {
  enrichEventsWithCctvLocations,
  mapApiEventToUi,
  type CctvLocationMap,
  type ApiEventRow,
  type PoolNameMap,
} from './eventMappers';
import { poolApi } from './poolApi';
import { notifySafetyAlertDispatchLogUpdated } from './safetyAlertDispatchService';
import type { Event as UiEvent } from '../contexts/EventContext';
import type { CentralRiskEventBbox } from '../components/central-monitoring/centralRiskEventTypes';

/** trailing slash — 307 리다이렉트 시 Authorization 유실 방지 */
const eventsBase = '/api/events/';

export interface OrphanEvidencePurgeResult {
  dry_run?: boolean;
  evidence_root?: string;
  kept_event_ids?: number;
  kept_dirs?: number;
  orphan_dirs?: number;
  orphan_bytes?: number;
  deleted_dirs?: number;
  deleted_bytes?: number;
  skipped_recent_staging?: number;
  staging_grace_sec?: number;
  samples?: Array<{ path?: string; name?: string; kind?: string; bytes?: number }>;
  errors?: string[];
}

function formatEvidenceBytes(n: number): string {
  if (!Number.isFinite(n) || n < 0) return '0 B';
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  if (n < 1024 * 1024 * 1024) return `${(n / (1024 * 1024)).toFixed(1)} MB`;
  return `${(n / (1024 * 1024 * 1024)).toFixed(2)} GB`;
}

export { formatEvidenceBytes };

export const eventService = {
  /** GET /api/pools → id → name 맵 (관제 이벤트 표시용) */
  fetchPoolNameMap: async (): Promise<PoolNameMap> => {
    const buildMap = (
      pools: Array<{ id?: number | string; name?: string; pool_name?: string; poolName?: string; code?: string }>
    ): PoolNameMap => {
      const map: PoolNameMap = {};
      for (const p of pools) {
        if (p?.id == null) continue;
        const label = String(p.name || p.pool_name || p.poolName || p.code || '').trim();
        map[String(p.id)] = label || `시설 #${p.id}`;
      }
      return map;
    };
    try {
      const response = await axiosInstance.get('/api/pools');
      const payload = response.data as
        | Array<{ id?: number | string; name?: string; pool_name?: string; poolName?: string; code?: string }>
        | {
            success?: boolean;
            data?: Array<{ id?: number | string; name?: string; pool_name?: string; poolName?: string; code?: string }>;
            items?: Array<{ id?: number | string; name?: string; pool_name?: string; poolName?: string; code?: string }>;
            pools?: Array<{ id?: number | string; name?: string; pool_name?: string; poolName?: string; code?: string }>;
          };
      const pools = Array.isArray(payload)
        ? payload
        : payload?.data ?? payload?.pools ?? payload?.items ?? [];
      return buildMap(pools);
    } catch {
      try {
        const res = await poolApi.getPools();
        const pools = (res.success && res.data ? res.data : []) as {
          id: number;
          name?: string;
          code?: string;
        }[];
        return buildMap(pools);
      } catch {
        return {};
      }
    }
  },

  fetchCctvLocationMap: async (): Promise<CctvLocationMap> => {
    try {
      const response = await axiosInstance.get('/api/cctvs/');
      const payload = response.data as
        | Array<{ id?: number | string; location?: string; name?: string }>
        | {
            data?: Array<{ id?: number | string; location?: string; name?: string }>;
            items?: Array<{ id?: number | string; location?: string; name?: string }>;
            cctvs?: Array<{ id?: number | string; location?: string; name?: string }>;
          };
      const rows = Array.isArray(payload)
        ? payload
        : payload?.data ?? payload?.items ?? payload?.cctvs ?? [];
      const map: CctvLocationMap = {};
      for (const row of rows) {
        if (row?.id == null) continue;
        const label = String(row.location || '').trim();
        if (!label) continue;
        map[String(row.id)] = label;
        const code = typeof (row as { code?: unknown }).code === 'string' ? String((row as { code?: string }).code).trim() : '';
        const poolId =
          (row as { pool_id?: unknown }).pool_id != null
            ? String((row as { pool_id?: unknown }).pool_id)
            : '';
        // 시설마다 같은 code(CCTV-1)가 있어 전역 code: 키는 서산/과천 위치를 섞음
        if (code && poolId) {
          map[`pool:${poolId}:code:${code.toUpperCase()}`] = label;
        }
      }
      return map;
    } catch {
      return {};
    }
  },

  createEvent: async (poolId: number, type: string, videoUrl: string): Promise<Event> => {
    const response = await axiosInstance.post(eventsBase, {
      pool_id: poolId,
      type,
      video_url: videoUrl,
    });
    return response.data;
  },

  /** 관제 확인 (P1-08) */
  /** DB ack_id 우선, 없으면 client_event_id / id */
  resolveAckId: (event: {
    ack_id?: string | number;
    id?: string | number;
    client_event_id?: string | null;
  }): string | number => {
    if (event.ack_id != null) return event.ack_id;
    if (typeof event.id === 'number' || /^\d+$/.test(String(event.id))) return event.id as number;
    return event.client_event_id || event.id || '';
  },

  ackEvent: async (
    eventId: string | number,
    falsePositive: boolean,
    note?: string
  ): Promise<void> => {
    await axiosInstance.post(`${eventsBase}/${eventId}/ack`, {
      false_positive: falsePositive,
      note,
    });
  },

  resolveEvent: async (
    eventId: string | number,
    resolveType: 'manual' | 'false_positive' | 'timeout' | 'duplicate' = 'manual',
    details?: string
  ): Promise<void> => {
    await axiosInstance.post(`${eventsBase}/${eventId}/resolve`, null, {
      params: { resolve_type: resolveType, details },
    });
  },

  setAutoResolveTimer: async (eventId: number, duration: number): Promise<void> => {
    await axiosInstance.post(`${eventsBase}/${eventId}/auto-resolve`, { duration });
  },

  getEventStatus: async (eventId: number): Promise<{ status: string; remainingTime?: number }> => {
    const response = await axiosInstance.get(`${eventsBase}/${eventId}/status`);
    return response.data;
  },

  deleteEvent: async (eventId: string | number): Promise<void> => {
    await axiosInstance.delete(`${eventsBase}${eventId}`);
  },

  bulkDelete: async (options: {
    eventIds?: string[];
    all?: boolean;
    poolId?: number;
  }): Promise<number> => {
    const res = await axiosInstance.post<{ deleted?: number }>(`${eventsBase}bulk-delete`, {
      event_ids: options.eventIds,
      all: options.all ?? false,
      pool_id: options.poolId,
    });
    return Number(res.data?.deleted ?? 0);
  },

  /** 미해결 이벤트 일괄 해결 (처리 중 카운트 → 0) */
  bulkResolveActive: async (options?: {
    poolId?: number;
    note?: string;
  }): Promise<number> => {
    const res = await axiosInstance.post<{ resolved?: number }>(`${eventsBase}bulk-resolve`, {
      all_active: true,
      pool_id: options?.poolId,
      note: options?.note ?? '대시보드에서 미해결 알림 일괄 확인',
    });
    return Number(res.data?.resolved ?? 0);
  },

  /**
   * bulk-resolve 실패·0건일 때: 최근 이벤트를 조회해 미해결건을 개별 ack.
   * (백엔드 미반영·상태값 불일치 대비)
   */
  clearActiveIncidentsReliably: async (options?: {
    poolId?: number;
    note?: string;
    limit?: number;
  }): Promise<number> => {
    const note = options?.note ?? '대시보드에서 미해결 알림 일괄 확인';
    let resolved = 0;
    try {
      resolved = await eventService.bulkResolveActive({
        poolId: options?.poolId,
        note,
      });
    } catch (e) {
      console.warn('bulk-resolve 실패, 개별 확인으로 재시도:', e);
    }

    const rows = await eventService.getEventsForUi(options?.poolId, options?.limit ?? 300);
    for (const ev of rows) {
      if (ev.status === 'resolved') continue;
      const ackId = eventService.resolveAckId({
        ack_id: ev.ack_id,
        id: ev.id,
        client_event_id: ev.client_event_id,
      });
      if (ackId === '' || ackId == null) continue;
      try {
        await eventService.ackEvent(ackId, false, note);
        resolved += 1;
      } catch {
        /* 이미 해결됐거나 삭제된 경우 무시 */
      }
    }
    return resolved;
  },

  getEvents: async (poolId?: number): Promise<Event[]> => {
    const response = await axiosInstance.get(eventsBase, {
      params: poolId != null ? { pool_id: poolId } : undefined,
    });
    return response.data;
  },

  /** 관제 EventContext용 — paginated API → UI 이벤트 */
  getEventsForUi: async (
    poolId?: number,
    limit = 100,
    poolNames?: PoolNameMap,
    cctvLocations?: CctvLocationMap,
  ): Promise<UiEvent[]> => {
    const response = await axiosInstance.get<{ items?: ApiEventRow[] } | ApiEventRow[]>(
      eventsBase,
      {
        params: {
          ...(poolId != null ? { pool_id: poolId } : {}),
          limit,
        },
      },
    );
    const data = response.data;
    const items = Array.isArray(data) ? data : data?.items ?? [];
    const mapped = items.map((row) => mapApiEventToUi(row, poolNames));
    return enrichEventsWithCctvLocations(mapped, cctvLocations);
  },

  /** 감지 시점 증거 영상 수집 시작 (DB 이벤트는 알림 전송 시 생성) */
  reportDrowningRisk: async (params: {
    poolId: number;
    cctvId: number;
    riskType?: string;
    triggerTs?: number;
    testMode?: boolean;
    message?: string;
    location?: string;
    /** bbox 포함 JPEG data URL 또는 base64 */
    snapshotBase64?: string;
    /** bbox 미포함 원본 JPEG (편집용) */
    snapshotRawBase64?: string;
    bboxes?: unknown[];
  }): Promise<{ evidence_scheduled?: boolean; snapshot_saved?: boolean }> => {
    let snapshot = params.snapshotBase64;
    if (snapshot && snapshot.startsWith('data:')) {
      const comma = snapshot.indexOf(',');
      snapshot = comma >= 0 ? snapshot.slice(comma + 1) : snapshot;
    }
    let snapshotRaw = params.snapshotRawBase64;
    if (snapshotRaw && snapshotRaw.startsWith('data:')) {
      const comma = snapshotRaw.indexOf(',');
      snapshotRaw = comma >= 0 ? snapshotRaw.slice(comma + 1) : snapshotRaw;
    }
    return axiosInstance.post('/api/events/report-drowning-risk', {
      pool_id: params.poolId,
      cctv_id: params.cctvId,
      risk_type: params.riskType,
      trigger_ts: params.triggerTs,
      test_mode: params.testMode ?? false,
      message: params.message,
      location: params.location,
      snapshot_base64: snapshot || undefined,
      snapshot_raw_base64: snapshotRaw || undefined,
      bboxes: params.bboxes,
    });
  },

  /** 알림 없이 닫을 때 임시 증거(영상·캡처) 삭제 */
  discardStagedEvidence: async (params: {
    poolId: number;
    cctvId: number;
    triggerTs: number;
  }): Promise<{ removed?: number }> => {
    return axiosInstance.post('/api/events/actions/discard-staged-evidence', {
      pool_id: params.poolId,
      cctv_id: params.cctvId,
      trigger_ts: params.triggerTs,
    });
  },

  /** 이벤트 리스트에 없는 gateway_evidence 고아 폴더 미리보기 */
  previewOrphanEvidence: async (): Promise<OrphanEvidencePurgeResult> => {
    const { data } = await axiosInstance.get<OrphanEvidencePurgeResult>(
      '/api/events/actions/orphan-evidence'
    );
    return data;
  },

  /** 이벤트 리스트에 남은 증거만 남기고 미연결 증거 일괄 삭제 */
  purgeOrphanEvidence: async (): Promise<OrphanEvidencePurgeResult> => {
    const { data } = await axiosInstance.post<OrphanEvidencePurgeResult>(
      '/api/events/actions/orphan-evidence/purge'
    );
    return data;
  },

  /** 팝업 bbox 편집을 스테이징에 저장 (이벤트 ID 없을 때) */
  uploadStagedEvidenceSnapshot: async (params: {
    poolId: number;
    cctvId: number;
    triggerTs: number;
    snapshotBase64: string;
    snapshotRawBase64?: string;
    bboxes?: unknown[];
  }): Promise<{ saved?: boolean }> => {
    let snapshot = params.snapshotBase64;
    if (snapshot.startsWith('data:')) {
      const comma = snapshot.indexOf(',');
      snapshot = comma >= 0 ? snapshot.slice(comma + 1) : snapshot;
    }
    let snapshotRaw = params.snapshotRawBase64;
    if (snapshotRaw && snapshotRaw.startsWith('data:')) {
      const comma = snapshotRaw.indexOf(',');
      snapshotRaw = comma >= 0 ? snapshotRaw.slice(comma + 1) : snapshotRaw;
    }
    return axiosInstance.post('/api/events/actions/staged-evidence-snapshot', {
      pool_id: params.poolId,
      cctv_id: params.cctvId,
      trigger_ts: params.triggerTs,
      snapshot_base64: snapshot,
      snapshot_raw_base64: snapshotRaw || undefined,
      bboxes: params.bboxes,
    });
  },

  sendPoolSafetySms: async (params: {
    poolId: number;
    message: string;
    phoneNumbers?: string[];
  }): Promise<void> => {
    await axiosInstance.post('/api/events/actions/safety-sms', {
      pool_id: params.poolId,
      message: params.message,
      phone_numbers: params.phoneNumbers,
    });
  },

  sendPoolSafetyAlert: async (params: {
    poolId: number;
    message: string;
    phoneNumbers?: string[];
    channels?: Array<'sms' | 'kakao' | 'wearable'>;
    alertLevel: 'warning' | 'danger';
    eventId?: number;
    cctvId?: number;
    location?: string;
    riskType?: string;
    triggerTs?: number;
    /** 이벤트 발생 시점 캡처 (data URL 또는 base64) — 스테이징 누락 보강 */
    snapshotBase64?: string;
    /** bbox 미포함 원본 (편집용) */
    snapshotRawBase64?: string;
    bboxes?: unknown[];
    /** false면 이벤트 DB 미저장 (미지정 시 서버 기본: 저장) */
    recordEvent?: boolean;
    /** 수동 알림 메시지 전용 — 이벤트 미저장, 발송 이력만 */
    operatorMessageOnly?: boolean;
    /** false면 현장 안전 알림 발송 이력 미저장 (미지정 시 저장) */
    recordDispatchLog?: boolean;
    /**
     * true면 관제실 경광 재시작 생략.
     * 위험 이벤트 팝업에서 이미 울린 뒤 「전송」할 때 사용.
     */
    skipControlRoomSiren?: boolean;
  }): Promise<{
    event_id?: number;
    evidence_scheduled?: boolean;
    success?: boolean;
    accepted?: boolean;
    partial?: boolean;
    detail?: string;
    channel_errors?: string[];
    channels?: Record<string, unknown>;
    /** 캡처+메시지 합성 JPEG (카카오톡 수동 첨부용) */
    share_image_path?: string;
    share_image_dir?: string;
    share_image_filename?: string;
    share_image_folder_opened?: boolean;
    kakao_bot_ok?: boolean;
    kakao_bot_detail?: string;
    kakao_bot?: Record<string, unknown>;
  }> => {
    let snapshot = params.snapshotBase64;
    if (snapshot && snapshot.startsWith('data:')) {
      const comma = snapshot.indexOf(',');
      snapshot = comma >= 0 ? snapshot.slice(comma + 1) : snapshot;
    }
    let snapshotRaw = params.snapshotRawBase64;
    if (snapshotRaw && snapshotRaw.startsWith('data:')) {
      const comma = snapshotRaw.indexOf(',');
      snapshotRaw = comma >= 0 ? snapshotRaw.slice(comma + 1) : snapshotRaw;
    }
    const response = await axiosInstance.post<{
      event_id?: number;
      evidence_scheduled?: boolean;
      success?: boolean;
      accepted?: boolean;
      partial?: boolean;
      detail?: string;
      channel_errors?: string[];
      channels?: Record<string, unknown>;
      share_image_path?: string;
      share_image_dir?: string;
      share_image_filename?: string;
      share_image_folder_opened?: boolean;
      kakao_bot_ok?: boolean;
      kakao_bot_detail?: string;
      kakao_bot?: Record<string, unknown>;
    }>(
      '/api/events/actions/safety-alert',
      {
        pool_id: params.poolId,
        message: params.message,
        phone_numbers: params.phoneNumbers,
        channels: params.channels,
        alert_level: params.alertLevel,
        event_id: params.eventId,
        cctv_id: params.cctvId,
        location: params.location,
        risk_type: params.riskType,
        trigger_ts: params.triggerTs,
        snapshot_base64: snapshot || undefined,
        snapshot_raw_base64: snapshotRaw || undefined,
        bboxes: params.bboxes,
        record_event: params.recordEvent,
        operator_message_only: params.operatorMessageOnly,
        record_dispatch_log: params.recordDispatchLog,
        skip_control_room_siren: params.skipControlRoomSiren,
      },
    );
    if (params.recordDispatchLog !== false) {
      notifySafetyAlertDispatchLogUpdated();
    }
    return response.data;
  },

  getRiskSnapshotMeta: async (
    eventId: number | string
  ): Promise<{
    event_id: number;
    bboxes: CentralRiskEventBbox[];
    has_raw: boolean;
    image_url: string;
    raw_image_url: string;
    edited_at?: string;
    saved_at?: string;
    scenario_profile?: string | null;
    detection_scenario?: string | null;
    risk_type?: string | null;
    risk_label?: string | null;
    risk_labels?: string[];
    message?: string | null;
    pool_id?: number | null;
  }> => {
    const res = await axiosInstance.get(`/api/events/${eventId}/risk-snapshot-meta`);
    return res.data;
  },

  updateRiskSnapshot: async (params: {
    eventId: number | string;
    snapshotBase64: string;
    snapshotRawBase64?: string;
    bboxes: unknown[];
    message?: string;
  }): Promise<{
    success?: boolean;
    image_url?: string;
    message?: string;
    risk_label?: string;
    risk_labels?: string[];
  }> => {
    let snapshot = params.snapshotBase64;
    if (snapshot.startsWith('data:')) {
      const comma = snapshot.indexOf(',');
      snapshot = comma >= 0 ? snapshot.slice(comma + 1) : snapshot;
    }
    let snapshotRaw = params.snapshotRawBase64;
    if (snapshotRaw && snapshotRaw.startsWith('data:')) {
      const comma = snapshotRaw.indexOf(',');
      snapshotRaw = comma >= 0 ? snapshotRaw.slice(comma + 1) : snapshotRaw;
    }
    const res = await axiosInstance.put(`/api/events/${params.eventId}/risk-snapshot`, {
      snapshot_base64: snapshot,
      snapshot_raw_base64: snapshotRaw || undefined,
      bboxes: params.bboxes,
      message: params.message,
    });
    return res.data;
  },
};
